import {AnimatePresence, m} from 'framer-motion';
import {Fragment} from 'react';
import {opacityAnimation} from '@common/ui/animation/opacity-animation';
import {Skeleton} from '@common/ui/skeleton/skeleton';
import {useProducts} from '@common/billing/pricing-table/use-products';
import {Product} from '@common/billing/product';
import {Price} from '@common/billing/price';
import {
  findBestPrice,
  UpsellBillingCycle,
} from '@common/billing/pricing-table/find-best-price';
import {useAuth} from '@common/auth/use-auth';
import clsx from 'clsx';
import {Trans} from '@common/i18n/trans';
import {FormattedPrice} from '@common/i18n/formatted-price';
import {Button} from '@common/ui/buttons/button';
import {Link} from 'react-router-dom';
import {setInLocalStorage} from '@common/utils/hooks/local-storage';
import {ProductFeatureList} from '@common/billing/pricing-table/product-feature-list';
import {usePaymentMethods} from '@common/billing/use-payment-methods';
import {useBootstrapData} from '@common/core/bootstrap-data/bootstrap-data-context';
import type {BootstrapData} from '@common/core/bootstrap-data/bootstrap-data';
import useGeoLocation from 'react-ipgeolocation';
import {getAuthPagePathWithRedirectUri} from '@common/auth/auth-redirects';
import {
  paymentMethodDisplayNames,
  paymentMethodLogos,
  wideLogoMethods,
} from '@common/billing/pricing-table/payment-method-logos';

interface PricingTableProps {
  // when omitted, each plan shows its default (or first) price instead of a
  // billing-cycle controlled one — used by the pricing page which has no
  // period switch. The landing page still passes a cycle.
  selectedCycle?: UpsellBillingCycle;
  className?: string;
  productLoader?: string;
  // 'row' = original centered flex row of plan cards (landing page);
  // 'grid' = responsive wrapping grid with ONE CARD PER OPERATOR
  //          (pricing page): 3 per row mobile, 4 tablet, 5 desktop
  layout?: 'row' | 'grid';
}

interface LegacyUpgradeRouteOptions {
  plan: Product;
  price?: Price;
  paymentMethod?: string;
  isLoggedIn: boolean;
  isSubscribed: boolean;
  environment?: BootstrapData['environment'];
}

function getLegacyUpgradeRoute({
  plan,
  price,
  paymentMethod,
  isLoggedIn,
  isSubscribed,
  environment,
}: LegacyUpgradeRouteOptions) {
  if (isSubscribed) {
    return `/billing/change-plan/${plan.id}/${price?.id}/confirm`;
  }
  if (paymentMethod === 'MTN_sd') {
    if (!environment?.CONSENT_GATEWAY_URL) {
      console.error('CONSENT_GATEWAY_URL is not set');
    }
    return environment?.CONSENT_GATEWAY_URL;
  }
  if (!isLoggedIn) {
    const checkoutRoute = getCheckoutRoute(plan, price, paymentMethod);
    return checkoutRoute
      ? getAuthPagePathWithRedirectUri('/register', checkoutRoute)
      : '/register';
  }
  if (isLoggedIn && !plan.free) {
    return getCheckoutRoute(plan, price, paymentMethod);
  }
}

function getCheckoutRoute(plan: Product, price?: Price, paymentMethod?: string) {
  if (!price) {
    return;
  }
  const route = `/checkout/${plan.id}/${price.id}`;
  return paymentMethod ? `${route}/${paymentMethod}` : route;
}
export function PricingTable({
  selectedCycle,
  className,
  productLoader,
  layout = 'row',
}: PricingTableProps) {
  const query = useProducts(productLoader);
  return (
    <div
      className={clsx(
        layout === 'grid'
          ? 'grid grid-cols-3 items-stretch gap-8 pb-20 md:grid-cols-4 md:gap-12 lg:grid-cols-5 lg:gap-16'
          : 'flex flex-col items-stretch gap-24 overflow-x-auto overflow-y-visible pb-20 md:flex-row md:justify-center',
        className,
      )}
    >
      <AnimatePresence initial={false} mode="wait">
        {query.data ? (
          <PlanList
            key="plan-list"
            plans={query.data.products}
            selectedPeriod={selectedCycle}
            layout={layout}
          />
        ) : (
          <SkeletonLoader key="skeleton-loader" />
        )}
      </AnimatePresence>
    </div>
  );
}

interface PlanListProps {
  plans: Product[];
  selectedPeriod?: UpsellBillingCycle;
  layout?: 'row' | 'grid';
}

type PaymentMethod = ReturnType<typeof usePaymentMethods>[number];

// one grid card = one operator (payment method) on one plan price. The free
// plan gets a card without price/method.
interface GridPlanEntry {
  plan: Product;
  price?: Price;
  method?: PaymentMethod;
  isHiddenByCountry?: boolean;
}

function normalizePhoneValue(value: string) {
  return value.replace(/[^\d+]/g, '');
}

function parsePhoneNumberPrefixes(phoneNumberPrefixes?: string) {
  if (!phoneNumberPrefixes) {
    return [];
  }

  return phoneNumberPrefixes
    .split(',')
    .map(prefix => normalizePhoneValue(prefix.trim()))
    .filter(Boolean);
}

// fixed display priority for grid cards AFTER the phone-match and
// recommended tiers: visa/card, stc, mobily, zain ksa, lebara, redbull,
// mtn, sudani. Ids not listed sort last (stable, keeping position/id order).
const OPERATOR_ORDER = [
  'stripe', // visa / card payments
  'stc',
  'mobily',
  'phonesub', // zain ksa
  'lebara',
  'redbull',
  'MTN_sd',
  'sudani_spay',
  'zain_sd',
  'paypal',
];

// clearer adverbial period labels for the pricing cards:
// "1 ر.س / يومياً" instead of the terse "/ يوم"
export const INTERVAL_LABELS: Record<string, string> = {
  day: 'يومياً',
  week: 'أسبوعياً',
  month: 'شهرياً',
  year: 'سنوياً',
};

// short label shown inside the payment method tile, e.g. "Zain KSA" -> "ZK"
function getMethodInitials(name: string) {
  return name
    .split(' ')
    .map(word => word[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

function MethodLogoTile({method}: {method: PaymentMethod}) {
  const logo = paymentMethodLogos[method.id];
  return (
    <div
      className={clsx(
        'flex h-42 flex-shrink-0 items-center justify-center rounded-xl border p-6',
        wideLogoMethods.has(method.id) ? 'w-72' : 'w-42',
        logo ? 'bg-white' : 'bg-chip text-xs font-extrabold uppercase',
      )}
    >
      {logo ? (
        <img
          src={logo}
          alt={method.name}
          className="max-h-full max-w-full object-contain"
          loading="lazy"
        />
      ) : (
        getMethodInitials(method.name)
      )}
    </div>
  );
}

function PlanList({plans, selectedPeriod, layout = 'row'}: PlanListProps) {
  const {isLoggedIn, isSubscribed, user, hasPermission} = useAuth();
  const isAdmin = hasPermission('admin');
  const {data: {environment}} = useBootstrapData();
  const ipLocation = useGeoLocation();
  // with no billing-cycle selector, fall back to the plan's default price
  // (or its first price) so every plan can still render
  const getDisplayPrice = (plan: Product): Price | undefined => {
    if (selectedPeriod) {
      return findBestPrice(selectedPeriod, plan.prices);
    }
    return (
      plan.prices?.find(price => price.default) ?? plan.prices?.[0]
    );
  };
  // the embedded page payload is ordered by position asc, but background
  // react-query refetches of billing/products come back ordered by id desc —
  // without a deterministic client-side sort the cards visibly reorder when
  // the refetched data replaces the initial payload
  const filteredPlans = [...plans]
    .sort(
      (a, b) => (a.position ?? 0) - (b.position ?? 0) || a.id - b.id,
    )
    .filter(plan => {
      if (plan.hidden) return false;

      const price = getDisplayPrice(plan);
      if (!price && !plan.free) {
        return false;
      }

      return true;
    });
  const paymentMethods = usePaymentMethods();
  const userCountryCode =
    (
      ipLocation.country ||
      user?.country ||
      ''
    )
      .toLowerCase()
      .trim();
  const userPhone = normalizePhoneValue(user?.phone || '');

  const isPaymentMethodHiddenByCountry = (method: PaymentMethod) => {
    const gatewayCountryCode = (method.country || 'all').toLowerCase().trim();
    return (
      gatewayCountryCode !== 'all' &&
      !!userCountryCode &&
      gatewayCountryCode !== userCountryCode
    );
  };

  const paymentMethodMatchesUserPhone = (method?: PaymentMethod) => {
    if (!userPhone || !method) return false;
    return parsePhoneNumberPrefixes(method.phone_number_prefixes).some(
      prefix => userPhone.startsWith(prefix),
    );
  };

  const planHasPhonePrefixMatch = (plan: Product) => {
    const price = getDisplayPrice(plan);
    if (!userPhone || !price) return false;

    return paymentMethods
      .filter(
        method =>
          method.enabled && (price.paymentMethods || []).includes(method.id),
      )
      .some(paymentMethodMatchesUserPhone);
  };

  const sortedPlans = filteredPlans
    .map((plan, index) => ({
      plan,
      index,
      hasPhonePrefixMatch: planHasPhonePrefixMatch(plan),
    }))
    .sort(
      (a, b) =>
        Number(b.hasPhonePrefixMatch) - Number(a.hasPhonePrefixMatch) ||
        a.index - b.index,
    )
    .map(({plan}) => plan);

  const getUpgradeRoute = (plan: Product, price?: Price, paymentMethod?: string) => {
    const externalLandingUrl = price?.external_landing_page_url?.trim();
    if (externalLandingUrl) {
      return externalLandingUrl;
    }
    return getLegacyUpgradeRoute({
      plan,
      price,
      paymentMethod,
      isLoggedIn,
      isSubscribed,
      environment,
    });
  }

  if (layout === 'grid') {
    // flatten plans into one card per operator: every enabled, country-visible
    // payment method across the plan's prices becomes its own card. A method
    // appearing on several prices of the same plan is shown once, preferring
    // the default price.
    const entries: GridPlanEntry[] = [];
    for (const plan of sortedPlans) {
      if (plan.free) {
        // keep the price so guest onboarding + change-plan routes stay intact
        // for free plans that carry a price row (mirrors the row layout)
        entries.push({plan, price: getDisplayPrice(plan)});
        continue;
      }
      const seenMethods = new Set<string>();
      let hasEnabledMethod = false;
      const prices = [...(plan.prices ?? [])].sort(
        (a, b) => Number(!!b.default) - Number(!!a.default),
      );
      for (const price of prices) {
        for (const method of paymentMethods) {
          if (!method.enabled) continue;
          if (!(price.paymentMethods || []).includes(method.id)) continue;
          hasEnabledMethod = true;
          if (seenMethods.has(method.id)) continue;
          const isHiddenByCountry = isPaymentMethodHiddenByCountry(method);
          if (isHiddenByCountry && !isAdmin) continue;
          seenMethods.add(method.id);
          entries.push({plan, price, method, isHiddenByCountry});
        }
      }
      if (seenMethods.size === 0) {
        // mirror the row-layout guard: a plan whose enabled operators are all
        // country-blocked for this visitor stays hidden entirely; only plans
        // with NO enabled operator at all render the plain fallback card
        if (hasEnabledMethod && !isAdmin) continue;
        entries.push({plan, price: getDisplayPrice(plan)});
      }
    }

    // card order: the visitor's own operator (matched by phone prefix) first,
    // then recommended plans, then the fixed OPERATOR_ORDER priority.
    // sort() is stable, so remaining ties keep the position/id order.
    const operatorRank = (entry: GridPlanEntry): number => {
      const id =
        entry.method?.id ??
        // fallback cards have no active method; rank by the price's
        // configured gateway so e.g. the card-payment plan still sorts
        // into the visa slot while stripe is disabled
        entry.price?.paymentMethods?.[0];
      const index = id ? OPERATOR_ORDER.indexOf(id) : -1;
      return index === -1 ? OPERATOR_ORDER.length : index;
    };
    entries.sort(
      (a, b) =>
        Number(paymentMethodMatchesUserPhone(b.method)) -
          Number(paymentMethodMatchesUserPhone(a.method)) ||
        Number(!!b.plan.recommended) - Number(!!a.plan.recommended) ||
        operatorRank(a) - operatorRank(b),
    );

    return (
      <Fragment>
        {entries.map(({plan, price, method, isHiddenByCountry}) => {
          const route = getUpgradeRoute(plan, price, method?.id);
          return (
            <m.div
              key={`${plan.id}-${price?.id ?? 0}-${method?.id ?? 'free'}`}
              {...opacityAnimation}
              className={clsx(
                'relative flex w-full flex-col items-center gap-8 rounded-2xl border bg p-10 text-center md:p-14 lg:p-16',
                plan.recommended
                  ? 'border-primary bg-gradient-to-b from-primary/10 via-transparent to-transparent pt-20 shadow-[0_18px_50px_-22px_rgb(var(--be-primary)/0.55)]'
                  : 'shadow-lg',
              )}
            >
              {plan.recommended && (
                <div className="absolute -top-10 left-10 whitespace-nowrap rounded-full bg-primary px-8 py-2 text-[10px] font-bold text-on-primary md:text-xs">
                  <Trans message="الأكثر شعبية" />
                </div>
              )}
              {/* operators without artwork show name-only, no initials tile */}
              {method && paymentMethodLogos[method.id] ? (
                <MethodLogoTile method={method} />
              ) : null}
              {/* wraps instead of truncating so long plan names like
                  "البطاقة البنكية" stay fully readable on narrow cards */}
              <div className="w-full break-words text-xs font-bold leading-snug md:text-sm">
                {method ? (
                  <Trans
                    message={
                      paymentMethodDisplayNames[method.id] ?? method.name
                    }
                  />
                ) : (
                  <Trans message={plan.name} />
                )}
              </div>
              {method ? (
                <div className="hidden w-full truncate text-xs text-muted md:block">
                  <Trans message={plan.name} />
                </div>
              ) : null}
              {price ? (
                <FormattedPrice
                  className="flex-wrap justify-center"
                  priceClassName="font-black text-xs md:text-base lg:text-lg"
                  // styled exactly like the amount so "1 ر.س / يومياً" reads
                  // as one clear statement instead of a faded suffix
                  periodClassName="font-black text-xs md:text-base lg:text-lg"
                  price={price}
                  hideDecimalsWhenWhole
                  periodLabel={
                    INTERVAL_LABELS[price.interval] ? (
                      <Trans message={INTERVAL_LABELS[price.interval]} />
                    ) : undefined
                  }
                />
              ) : (
                <div className="text-xs font-black md:text-base lg:text-lg">
                  <Trans message="مجاني" />
                </div>
              )}
              {isHiddenByCountry ? (
                <div className="text-xs font-semibold text-danger">
                  hidden by ip
                </div>
              ) : null}
              <Button
                variant={plan.recommended ? 'flat' : 'outline'}
                color="primary"
                size="xs"
                className="mt-auto w-full"
                elementType={route ? Link : undefined}
                disabled={!route}
                onClick={() => {
                  if (isLoggedIn || !price || !plan) return;
                  setInLocalStorage('be.onboarding.selected', {
                    productId: plan.id,
                    priceId: price.id,
                    ...(method ? {paymentMethod: method.id} : {}),
                  });
                }}
                to={route}
              >
                {plan.free ? (
                  <Trans message="ابدأ الآن" />
                ) : (
                  <Trans message="اشترك الآن" />
                )}
              </Button>
            </m.div>
          );
        })}
      </Fragment>
    );
  }

  return (
    <Fragment>
      {sortedPlans.map((plan, index) => {
        const isFirst = index === 0;
        const isLast = index === sortedPlans.length - 1;
        const price = getDisplayPrice(plan);
        const planPaymentMethods = paymentMethods.filter(
          method =>
            method.enabled && (price?.paymentMethods || []).includes(method.id),
        );
        const methodsWithCountryVisibility = planPaymentMethods.map(method => ({
          method,
          isHiddenByCountry: isPaymentMethodHiddenByCountry(method),
        }));
        const visibleMethodsForUser = methodsWithCountryVisibility.filter(
          ({isHiddenByCountry}) => isAdmin || !isHiddenByCountry,
        );

        if (!plan.free && !isAdmin && planPaymentMethods.length && !visibleMethodsForUser.length) {
          return null;
        }

        return (
          <m.div
            key={plan.id}
            {...opacityAnimation}
            className={clsx(
              'relative flex w-full flex-col rounded-2xl border bg px-24 py-28 md:min-w-240 md:max-w-350',
              plan.recommended
                ? 'border-primary bg-gradient-to-b from-primary/10 via-transparent to-transparent shadow-[0_18px_50px_-22px_rgb(var(--be-primary)/0.55)]'
                : 'shadow-lg',
              isFirst && 'ml-auto',
              isLast && 'mr-auto',
            )}
          >
            {plan.recommended && (
              <div className="absolute -top-12 left-20 whitespace-nowrap rounded-full bg-primary px-12 py-4 text-xs font-bold text-on-primary">
                <Trans message="الأكثر شعبية" />
              </div>
            )}
            <div className="mb-20">
              <div className="mb-8 text-lg font-bold">
                <Trans message={plan.name} />
              </div>
              {plan.description ? (
                <div className="text-xs text-muted">
                  <Trans message={plan.description} />
                </div>
              ) : null}
            </div>
            {price ? (
              <FormattedPrice
                priceClassName="font-black text-4xl"
                periodClassName="text-muted text-xs font-medium"
                price={price}
              />
            ) : (
              <div className="text-4xl font-black">
                <Trans message="مجاني" />
              </div>
            )}
            <ProductFeatureList product={plan} />
            <div className="mt-auto pt-28">
              {plan.free ? (
                <Button
                  variant={plan.recommended ? 'flat' : 'outline'}
                  color="primary"
                  className="w-full"
                  size="md"
                  elementType={getUpgradeRoute(plan, price) ? Link : undefined}
                  disabled={!getUpgradeRoute(plan, price)}
                  onClick={() => {
                    if (isLoggedIn || !price || !plan) return;
                    setInLocalStorage('be.onboarding.selected', {
                      productId: plan.id,
                      priceId: price.id,
                    });
                  }}
                  to={getUpgradeRoute(plan, price)}
                >
                  <Trans message="ابدأ الآن" />
                </Button>
              ) : (
                visibleMethodsForUser.map(({method, isHiddenByCountry}) => {
                  return (
                    <div
                      key={method.id}
                      className="mb-12 flex items-center gap-12"
                    >
                      <MethodLogoTile method={method} />
                      <div className="min-w-0 flex-auto">
                        <div className="truncate text-sm font-semibold">
                          {method.name}
                        </div>
                        {isHiddenByCountry ? (
                          <div className="text-xs font-semibold text-danger">
                            hidden by ip
                          </div>
                        ) : null}
                      </div>
                      <Button
                        variant={plan.recommended ? 'flat' : 'outline'}
                        color="primary"
                        size="xs"
                        elementType={
                          getUpgradeRoute(plan, price, method.id)
                            ? Link
                            : undefined
                        }
                        disabled={!getUpgradeRoute(plan, price, method.id)}
                        onClick={() => {
                          if (isLoggedIn || !price || !plan) return;
                          setInLocalStorage('be.onboarding.selected', {
                            productId: plan.id,
                            priceId: price.id,
                            paymentMethod: method.id,
                          });
                        }}
                        to={getUpgradeRoute(plan, price, method.id)}
                      >
                        <Trans message="اشترك الآن" />
                      </Button>
                    </div>
                  );
                })
              )}
              {!plan.free && (
                <div className="mt-14 text-center text-xs text-muted">
                  <Trans message="إلغاء في أي وقت · بدون رسوم خفية" />
                </div>
              )}
            </div>
          </m.div>
        );
      })}
    </Fragment>
  );
}

function SkeletonLoader() {
  return (
    <Fragment>
      <PlanSkeleton key="skeleton-1" />
      <PlanSkeleton key="skeleton-2" />
      <PlanSkeleton key="skeleton-3" />
    </Fragment>
  );
}

function PlanSkeleton() {
  return (
    <m.div
      {...opacityAnimation}
      className="w-full rounded-lg border px-28 py-90 shadow-lg md:max-w-350"
    >
      <Skeleton className="my-10" />
      <Skeleton className="mb-40" />
      <Skeleton className="mb-40 h-30" />
      <Skeleton className="mb-40 h-40" />
      <Skeleton className="mb-20" />
      <Skeleton />
      <Skeleton />
    </m.div>
  );
}
